import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Search, Package, AlertTriangle, Wrench, ArrowRightLeft, Trash2, Edit, CheckCircle, Send, RotateCcw, RefreshCw, Download, Clock, FileCheck, ArrowUpRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SparePartsPage() {
  const [spareParts, setSpareParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [summary, setSummary] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [pendingPOs, setPendingPOs] = useState([]);
  const [activeMainTab, setActiveMainTab] = useState("inventory");
  const [allIssues, setAllIssues] = useState([]);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showIssueDialog, setShowIssueDialog] = useState(false);
  const [showRepairSendDialog, setShowRepairSendDialog] = useState(false);
  const [showRepairReceiveDialog, setShowRepairReceiveDialog] = useState(false);
  const [showReceivePODialog, setShowReceivePODialog] = useState(false);
  const [selectedPOItem, setSelectedPOItem] = useState(null);
  
  // Add Stock dialog state (Admin only)
  const [showAddStockDialog, setShowAddStockDialog] = useState(false);
  const [addStockPart, setAddStockPart] = useState(null);
  const [addStockForm, setAddStockForm] = useState({ quantity: "", notes: "" });
  
  // Issue edit/delete states
  const [showEditIssueDialog, setShowEditIssueDialog] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [editIssueForm, setEditIssueForm] = useState({ quantity: "", purpose: "", issued_to: "" });
  
  // Repair edit state
  const [showEditRepairDialog, setShowEditRepairDialog] = useState(false);
  const [editingRepair, setEditingRepair] = useState(null);
  const [editRepairForm, setEditRepairForm] = useState({ quantity: "", repair_vendor: "", sent_date: "", expected_return_date: "", notes: "" });
  
  // Filter state for summary card clicks
  const [inventoryFilter, setInventoryFilter] = useState("all");
  
  // Form states - use empty strings instead of 0 for quantity fields
  const [newPart, setNewPart] = useState({ name: "", description: "", opening_qty: "", unit: "pcs", min_stock_level: "", location: "" });
  const [editPart, setEditPart] = useState({ name: "", description: "", min_stock_level: "", location: "" });
  const [issueForm, setIssueForm] = useState({ quantity: "", purpose: "", issued_to: "" });
  const [repairSendForm, setRepairSendForm] = useState({ quantity: "", repair_vendor: "", sent_date: "", expected_return_date: "", notes: "" });
  const [repairReceiveForm, setRepairReceiveForm] = useState({ repair_id: "", received_qty: "", received_date: "", notes: "" });
  const [receivePOForm, setReceivePOForm] = useState({ quantity: "", notes: "" });
  
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser?.role === 'admin';
  const isMaintenance = currentUser?.department === 'maintenance';
  const canEditDelete = isAdmin || isMaintenance;
  
  const fetchSpareParts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/spare-parts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSpareParts(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch spare parts");
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/spare-parts/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data);
    } catch (err) {
      console.error("Failed to fetch summary:", err);
    }
  }, []);
  
  const fetchAlerts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/spare-parts/alerts/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(response.data.alerts || []);
    } catch (err) {
      console.error("Failed to fetch alerts:", err);
    }
  }, []);
  
  const fetchPendingPOs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/spare-parts/pending-pos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingPOs(response.data || []);
    } catch (err) {
      console.error("Failed to fetch pending POs:", err);
    }
  }, []);

  const fetchAllIssues = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/spare-parts/issues/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllIssues(response.data || []);
    } catch (err) {
      console.error("Failed to fetch all issues:", err);
    }
  }, []);
  
  const fetchPartDetails = useCallback(async (partId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/spare-parts/${partId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPart(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch part details");
    }
  }, []);
  
  useEffect(() => {
    fetchSpareParts();
    fetchSummary();
    fetchAlerts();
    fetchPendingPOs();
    fetchAllIssues();
  }, [fetchSpareParts, fetchSummary, fetchAlerts, fetchPendingPOs, fetchAllIssues]);
  
  // Refresh all data
  const handleRefresh = async () => {
    setRefreshing(true);
    setError("");
    setSuccess("");
    try {
      await Promise.all([fetchSpareParts(), fetchSummary(), fetchAlerts(), fetchPendingPOs(), fetchAllIssues()]);
      if (selectedPart) {
        await fetchPartDetails(selectedPart.id);
      }
      setSuccess("Data refreshed successfully");
    } catch (err) {
      setError("Failed to refresh data");
    } finally {
      setRefreshing(false);
    }
  };
  
  // Receive PO item into spare parts
  const handleReceivePO = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/spare-parts/receive-from-po`, {
        po_id: selectedPOItem.po_id,
        item_index: selectedPOItem.item_index,
        quantity_received: parseInt(receivePOForm.quantity),
        notes: receivePOForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const result = response.data;
      const sparePartAction = result.spare_part?.action === 'created' ? 'created new' : 'updated';
      setSuccess(`Received ${receivePOForm.quantity} ${selectedPOItem.unit} - ${sparePartAction} spare part "${selectedPOItem.item_name}"`);
      
      setShowReceivePODialog(false);
      setSelectedPOItem(null);
      setReceivePOForm({ quantity: "", notes: "" });
      fetchPendingPOs();
      fetchSpareParts();
      fetchSummary();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to receive PO item");
    }
  };
  
  const openReceivePODialog = (poItem) => {
    setSelectedPOItem(poItem);
    setReceivePOForm({ quantity: poItem.pending_quantity.toString(), notes: "" });
    setShowReceivePODialog(true);
  };
  
  // Download Excel
  const handleDownloadExcel = () => {
    // Prepare data for Excel
    const data = spareParts.map(part => ({
      'Part Name': part.name,
      'Description': part.description || '',
      'Location': part.location || '',
      'Unit': part.unit,
      'Opening Qty': part.opening_qty || 0,
      'Total Received': part.total_received || 0,
      'Total Issued': part.total_issued || 0,
      'Sent for Repair': part.sent_for_repair || 0,
      'Returned from Repair': part.returned_from_repair || 0,
      'Current Stock': part.current_stock || 0,
      'Available Stock': part.available_stock || 0,
      'Min Stock Level': part.min_stock_level || 0,
      'Created By': part.created_by || '',
      'Created At': part.created_at ? new Date(part.created_at).toLocaleDateString() : ''
    }));
    
    // Convert to CSV (simple Excel compatible format)
    const headers = Object.keys(data[0] || {});
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => {
        const val = row[h];
        // Escape commas and quotes
        if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      }).join(','))
    ].join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `spare_parts_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    setSuccess("Excel file downloaded successfully");
  };
  
  // Helper to parse quantity (empty string becomes 0)
  const parseQty = (value) => {
    if (value === '' || value === null || value === undefined) return 0;
    const num = parseInt(value, 10);
    return isNaN(num) ? 0 : num;
  };
  
  const handleAddPart = async () => {
    try {
      const token = localStorage.getItem('token');
      const partData = {
        ...newPart,
        opening_qty: parseQty(newPart.opening_qty),
        min_stock_level: parseQty(newPart.min_stock_level)
      };
      await axios.post(`${API_URL}/api/spare-parts`, partData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Spare part added successfully");
      setShowAddDialog(false);
      setNewPart({ name: "", description: "", opening_qty: "", unit: "pcs", min_stock_level: "", location: "" });
      fetchSpareParts();
      fetchSummary();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add spare part");
    }
  };
  
  const handleEditPart = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/spare-parts/${selectedPart.id}`, {
        name: editPart.name,
        description: editPart.description,
        min_stock_level: editPart.min_stock_level ? parseInt(editPart.min_stock_level) : 0,
        location: editPart.location
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Spare part updated successfully");
      setShowEditDialog(false);
      fetchSpareParts();
      fetchPartDetails(selectedPart.id);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update spare part");
    }
  };
  
  const openEditDialog = () => {
    setEditPart({
      name: selectedPart.name || "",
      description: selectedPart.description || "",
      min_stock_level: selectedPart.min_stock_level?.toString() || "",
      location: selectedPart.location || ""
    });
    setShowEditDialog(true);
  };
  
  const handleDeletePart = async (partId) => {
    if (!window.confirm("Are you sure you want to delete this spare part and all its records?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/spare-parts/${partId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Spare part deleted successfully");
      setSelectedPart(null);
      fetchSpareParts();
      fetchSummary();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete spare part");
    }
  };

  const openAddStockDialog = (part) => {
    setAddStockPart(part);
    setAddStockForm({ quantity: "", notes: "" });
    setShowAddStockDialog(true);
  };

  const handleAddStock = async () => {
    const qty = parseInt(addStockForm.quantity);
    if (!qty || qty <= 0) {
      setError("Quantity must be greater than 0");
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/spare-parts/${addStockPart.id}/add-stock`, {
        quantity: qty,
        notes: addStockForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(response.data.message);
      setShowAddStockDialog(false);
      fetchSpareParts();
      fetchSummary();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add stock");
    }
  };
  
  const handleIssuePart = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/spare-parts/issue`, {
        spare_part_id: selectedPart.id,
        quantity: parseQty(issueForm.quantity),
        purpose: issueForm.purpose,
        issued_to: issueForm.issued_to
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Spare part issued successfully");
      setShowIssueDialog(false);
      setIssueForm({ quantity: "", purpose: "", issued_to: "" });
      fetchPartDetails(selectedPart.id);
      fetchSpareParts();
      fetchSummary();
      fetchAllIssues();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to issue spare part");
    }
  };
  
  // Edit issue entry
  const handleEditIssue = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/spare-parts/issue/${editingIssue.id}`, {
        quantity: parseQty(editIssueForm.quantity),
        purpose: editIssueForm.purpose,
        issued_to: editIssueForm.issued_to
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Issue record updated successfully");
      setShowEditIssueDialog(false);
      setEditingIssue(null);
      setEditIssueForm({ quantity: "", purpose: "", issued_to: "" });
      fetchPartDetails(selectedPart.id);
      fetchSpareParts();
      fetchSummary();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update issue record");
    }
  };
  
  // Delete issue entry
  const handleDeleteIssue = async (issueId) => {
    if (!window.confirm("Are you sure you want to delete this issue record? Stock will be restored.")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/spare-parts/issue/${issueId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Issue record deleted and stock restored");
      fetchPartDetails(selectedPart.id);
      fetchSpareParts();
      fetchSummary();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete issue record");
    }
  };
  
  // Open edit issue dialog
  const openEditIssueDialog = (issue) => {
    setEditingIssue(issue);
    setEditIssueForm({
      quantity: issue.quantity?.toString() || "",
      purpose: issue.purpose || "",
      issued_to: issue.issued_to || ""
    });
    setShowEditIssueDialog(true);
  };
  
  const handleSendForRepair = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/spare-parts/repair/send`, {
        spare_part_id: selectedPart.id,
        quantity: parseQty(repairSendForm.quantity),
        repair_vendor: repairSendForm.repair_vendor,
        sent_date: repairSendForm.sent_date,
        expected_return_date: repairSendForm.expected_return_date,
        notes: repairSendForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Spare part sent for repair");
      setShowRepairSendDialog(false);
      setRepairSendForm({ quantity: "", repair_vendor: "", sent_date: "", expected_return_date: "", notes: "" });
      fetchPartDetails(selectedPart.id);
      fetchSpareParts();
      fetchSummary();
      fetchAlerts();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send for repair");
    }
  };
  
  const handleReceiveFromRepair = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/spare-parts/repair/receive`, {
        repair_id: repairReceiveForm.repair_id,
        received_qty: parseQty(repairReceiveForm.received_qty),
        received_date: repairReceiveForm.received_date,
        notes: repairReceiveForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Spare part received from repair");
      setShowRepairReceiveDialog(false);
      setRepairReceiveForm({ repair_id: "", received_qty: "", received_date: "", notes: "" });
      fetchPartDetails(selectedPart.id);
      fetchSpareParts();
      fetchSummary();
      fetchAlerts();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to receive from repair");
    }
  };
  
  const handleEditRepair = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/spare-parts/repair/${editingRepair.id}`, {
        quantity: parseQty(editRepairForm.quantity),
        repair_vendor: editRepairForm.repair_vendor,
        sent_date: editRepairForm.sent_date,
        expected_return_date: editRepairForm.expected_return_date,
        notes: editRepairForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Repair updated successfully");
      setShowEditRepairDialog(false);
      setEditingRepair(null);
      fetchPartDetails(selectedPart.id);
      fetchSpareParts();
      fetchSummary();
      fetchAlerts();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update repair");
    }
  };

  const filteredParts = spareParts.filter(part => {
    const matchesSearch = part.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      part.description?.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (inventoryFilter === "all") return true;
    if (inventoryFilter === "low_stock") return part.current_stock > 0 && part.current_stock <= (part.min_stock_level || 0);
    if (inventoryFilter === "out_of_stock") return part.current_stock <= 0;
    if (inventoryFilter === "in_repair") return (part.sent_for_repair || 0) > 0;
    return true;
  });
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="spare-parts-title">Spare Parts Inventory</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage spare parts, repairs, and stock levels</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="outline" onClick={handleDownloadExcel} disabled={spareParts.length === 0} data-testid="download-excel-btn">
            <Download className="w-4 h-4 mr-2" />
            Download Excel
          </Button>
          {canEditDelete && (
            <Button onClick={() => setShowAddDialog(true)} data-testid="add-spare-part-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Spare Part
            </Button>
          )}
        </div>
      </div>
      
      {/* Messages */}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">{success}</div>}
      
      {/* Main Tabs - at the top for mobile accessibility */}
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="inventory" className="text-xs sm:text-sm px-1 sm:px-3">
            <Package className="w-3 h-3 mr-1 hidden sm:inline" />
            Inventory ({spareParts.length})
          </TabsTrigger>
          <TabsTrigger value="issued" data-testid="issued-tab" className="text-xs sm:text-sm px-1 sm:px-3">
            <ArrowUpRight className="w-3 h-3 mr-1 hidden sm:inline" />
            Issued ({allIssues.length})
          </TabsTrigger>
          <TabsTrigger value="pending-pos" className="text-xs sm:text-sm px-1 sm:px-3">
            <Clock className="w-3 h-3 mr-1 hidden sm:inline" />
            POs ({pendingPOs.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="inventory" className="mt-4 space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className={`cursor-pointer transition-all hover:shadow-md ${inventoryFilter === "all" ? "ring-2 ring-primary" : ""}`} onClick={() => { setInventoryFilter("all"); setActiveMainTab("inventory"); }} data-testid="card-total-parts">
          <CardContent className="p-4">
            <Package className="w-5 h-5 text-muted-foreground mb-2" />
            <p className="text-2xl font-bold">{summary.total_parts || 0}</p>
            <p className="text-xs text-muted-foreground">Total Parts</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-all hover:shadow-md ${inventoryFilter === "low_stock" ? "ring-2 ring-yellow-500" : ""} ${summary.low_stock > 0 ? "border-yellow-500" : ""}`} onClick={() => { setInventoryFilter("low_stock"); setActiveMainTab("inventory"); }} data-testid="card-low-stock">
          <CardContent className="p-4">
            <AlertTriangle className="w-5 h-5 text-yellow-500 mb-2" />
            <p className="text-2xl font-bold">{summary.low_stock || 0}</p>
            <p className="text-xs text-muted-foreground">Low Stock</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-all hover:shadow-md ${inventoryFilter === "out_of_stock" ? "ring-2 ring-red-500" : ""} ${summary.out_of_stock > 0 ? "border-red-500" : ""}`} onClick={() => { setInventoryFilter("out_of_stock"); setActiveMainTab("inventory"); }} data-testid="card-out-of-stock">
          <CardContent className="p-4">
            <AlertTriangle className="w-5 h-5 text-red-500 mb-2" />
            <p className="text-2xl font-bold">{summary.out_of_stock || 0}</p>
            <p className="text-xs text-muted-foreground">Out of Stock</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-all hover:shadow-md ${inventoryFilter === "in_repair" ? "ring-2 ring-blue-500" : ""}`} onClick={() => { setInventoryFilter("in_repair"); setActiveMainTab("inventory"); }} data-testid="card-in-repair">
          <CardContent className="p-4">
            <Wrench className="w-5 h-5 text-blue-500 mb-2" />
            <p className="text-2xl font-bold">{summary.pending_repairs || 0}</p>
            <p className="text-xs text-muted-foreground">In Repair</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-all hover:shadow-md ${pendingPOs.length > 0 ? "border-orange-500" : ""}`} onClick={() => { setActiveMainTab("pending-pos"); setInventoryFilter("all"); }} data-testid="card-pending-pos">
          <CardContent className="p-4">
            <Clock className="w-5 h-5 text-orange-500 mb-2" />
            <p className="text-2xl font-bold">{pendingPOs.length}</p>
            <p className="text-xs text-muted-foreground">Pending POs</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer transition-all hover:shadow-md" onClick={() => { document.getElementById('alerts-section')?.scrollIntoView({ behavior: 'smooth' }); }} data-testid="card-alerts">
          <CardContent className="p-4">
            <AlertTriangle className="w-5 h-5 text-purple-500 mb-2" />
            <p className="text-2xl font-bold">{alerts.length}</p>
            <p className="text-xs text-muted-foreground">Alerts</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Alerts Section */}
      {alerts.length > 0 && (
        <Card id="alerts-section" className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              Alerts & Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(showAllAlerts ? alerts : alerts.slice(0, 5)).map((alert, idx) => (
                <div key={idx} className={`flex items-center gap-2 p-2 rounded ${alert.severity === 'high' ? 'bg-red-100 dark:bg-red-900' : 'bg-yellow-100 dark:bg-yellow-900'}`}>
                  <Badge variant={alert.severity === 'high' ? 'destructive' : 'secondary'}>{alert.type.replace('_', ' ')}</Badge>
                  <span className="text-sm">{alert.message}</span>
                </div>
              ))}
              {alerts.length > 5 && (
                <button
                  onClick={() => setShowAllAlerts(!showAllAlerts)}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium mt-1"
                  data-testid="toggle-alerts-btn"
                >
                  {showAllAlerts ? 'Show less' : `...and ${alerts.length - 5} more alerts`}
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
          {/* Main Content - Inventory */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Parts List */}
            <Card className="lg:col-span-1">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Spare Parts</CardTitle>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search parts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchQuery(searchQuery)}
                    className="pl-9"
                    data-testid="search-spare-parts"
                  />
                  {searchQuery && (
                    <button className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setSearchQuery("")}>
                      <span className="text-xs">✕</span>
                    </button>
                  )}
                </div>
                {searchQuery && filteredParts.length > 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">{filteredParts.length} of {spareParts.length} parts</div>
                )}
                {inventoryFilter !== "all" && (
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="secondary" className="text-xs">
                      Filter: {inventoryFilter.replace('_', ' ')}
                    </Badge>
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setInventoryFilter("all")}>Clear</Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto">
                {loading ? (
                  <p className="text-center py-8 text-muted-foreground">Loading...</p>
                ) : filteredParts.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No spare parts found</p>
                ) : (
                  <div className="space-y-2">
                    {filteredParts.map((part) => (
                      <div
                        key={part.id}
                        className={`p-3 rounded-lg border transition-colors ${selectedPart?.id === part.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}
                        data-testid={`spare-part-${part.id}`}
                      >
                        <div 
                          className="cursor-pointer"
                          onClick={() => fetchPartDetails(part.id)}
                        >
                          <p className="font-medium">{part.name}</p>
                          <p className="text-xs text-muted-foreground">{part.location || 'No location'}</p>
                        </div>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <Badge variant={part.current_stock <= 0 ? 'destructive' : part.current_stock <= (part.min_stock_level || 0) ? 'secondary' : 'outline'}>
                            {part.current_stock} {part.unit}
                          </Badge>
                          {part.sent_for_repair > 0 && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">In Repair: {part.sent_for_repair}</Badge>
                          )}
                          <div className="ml-auto flex gap-1">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPart(part);
                                setIssueForm({ quantity: "", purpose: "", issued_to: "" });
                                setShowIssueDialog(true);
                              }}
                              data-testid={`quick-issue-${part.id}`}
                              disabled={part.available_stock <= 0}
                            >
                              <ArrowRightLeft className="w-3 h-3 mr-1" />
                              Issue
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPart(part);
                                setRepairSendForm({ quantity: "", repair_vendor: "", sent_date: new Date().toISOString().split('T')[0], expected_return_date: "", notes: "" });
                                setShowRepairSendDialog(true);
                              }}
                              data-testid={`quick-repair-${part.id}`}
                              disabled={part.current_stock <= 0}
                            >
                              <Wrench className="w-3 h-3 mr-1" />
                              Repair
                            </Button>
                            {isAdmin && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="h-7 px-2 text-xs bg-green-50 text-green-700 hover:bg-green-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAddStockDialog(part);
                                }}
                                data-testid={`add-stock-${part.id}`}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Stock
                              </Button>
                            )}
                          </div>
                        </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Part Details */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>{selectedPart?.name || 'Select a spare part'}</CardTitle>
                <CardDescription>{selectedPart?.description || 'Click on a part to view details'}</CardDescription>
              </div>
              {selectedPart && canEditDelete && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={openEditDialog} data-testid="edit-part-btn">
                    <Edit className="w-4 h-4" />
                  </Button>
                  {isAdmin && (
                    <Button variant="outline" size="sm" className="bg-green-50 text-green-700 hover:bg-green-100" onClick={() => openAddStockDialog(selectedPart)} data-testid="add-stock-detail-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Stock
                    </Button>
                  )}
                  {isAdmin && (
                    <Button variant="destructive" size="sm" onClick={() => handleDeletePart(selectedPart.id)} data-testid="delete-part-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          {selectedPart ? (
            <CardContent>
              {/* Stock Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Available Stock</p>
                  <p className="text-xl font-bold text-green-600">{selectedPart.available_stock} {selectedPart.unit}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Currently in Use</p>
                  <p className="text-xl font-bold text-purple-600">{selectedPart.current_stock} {selectedPart.unit}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Sent for Repair</p>
                  <p className="text-xl font-bold text-blue-600">{selectedPart.sent_for_repair || 0} {selectedPart.unit}</p>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 mb-6">
                <Button variant="outline" onClick={() => setShowIssueDialog(true)} data-testid="issue-part-btn">
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Issue Part
                </Button>
                <Button variant="outline" onClick={() => setShowRepairSendDialog(true)} data-testid="send-repair-btn">
                  <Send className="w-4 h-4 mr-2" />
                  Send for Repair
                </Button>
              </div>
              
              {/* Tabs for History */}
              <Tabs defaultValue="issues">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="issues">Issues ({selectedPart.issues?.length || 0})</TabsTrigger>
                  <TabsTrigger value="repairs">Repairs ({selectedPart.repairs?.length || 0})</TabsTrigger>
                </TabsList>
                
                <TabsContent value="issues" className="mt-4">
                  {selectedPart.issues?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Qty</TableHead>
                          <TableHead>Purpose</TableHead>
                          <TableHead>Issued By</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedPart.issues.map((issue) => (
                          <TableRow key={issue.id}>
                            <TableCell>{formatDate(issue.created_at)}</TableCell>
                            <TableCell>{issue.quantity}</TableCell>
                            <TableCell>{issue.purpose}</TableCell>
                            <TableCell>{issue.issued_by}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {canEditDelete && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditIssueDialog(issue)}
                                    data-testid={`edit-issue-${issue.id}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                )}
                                {isAdmin && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-destructive"
                                    onClick={() => handleDeleteIssue(issue.id)}
                                    data-testid={`delete-issue-${issue.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center py-8 text-muted-foreground">No issue records</p>
                  )}
                </TabsContent>
                
                <TabsContent value="repairs" className="mt-4">
                  {selectedPart.repairs?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Sent Date</TableHead>
                          <TableHead>Qty</TableHead>
                          <TableHead>Vendor</TableHead>
                          <TableHead>Expected</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedPart.repairs.map((repair) => (
                          <TableRow key={repair.id}>
                            <TableCell>{formatDate(repair.sent_date)}</TableCell>
                            <TableCell>{repair.quantity}</TableCell>
                            <TableCell>{repair.repair_vendor}</TableCell>
                            <TableCell>{formatDate(repair.expected_return_date)}</TableCell>
                            <TableCell>
                              <Badge variant={repair.status === 'sent' ? 'secondary' : 'outline'}>
                                {repair.status === 'sent' ? 'In Repair' : `Received (${repair.received_qty})`}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {repair.status === 'sent' && (
                                <div className="flex items-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid={`edit-repair-${repair.id}`}
                                    onClick={() => {
                                      setEditingRepair(repair);
                                      setEditRepairForm({
                                        quantity: repair.quantity,
                                        repair_vendor: repair.repair_vendor,
                                        sent_date: repair.sent_date,
                                        expected_return_date: repair.expected_return_date,
                                        notes: repair.notes || ""
                                      });
                                      setShowEditRepairDialog(true);
                                    }}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setRepairReceiveForm({ repair_id: repair.id, received_qty: repair.quantity, received_date: new Date().toISOString().split('T')[0], notes: "" });
                                      setShowRepairReceiveDialog(true);
                                    }}
                                  >
                                    <RotateCcw className="w-4 h-4 mr-1" />
                                    Receive
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center py-8 text-muted-foreground">No repair records</p>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          ) : (
            <CardContent>
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Package className="w-12 h-12 mb-4 opacity-20" />
                <p>Select a spare part to view details</p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
        </TabsContent>
        
        {/* Pending POs Tab */}
        <TabsContent value="pending-pos" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-500" />
                Pending Purchase Orders
              </CardTitle>
              <CardDescription>POs from Maintenance department awaiting receipt. Mark items as received to add them to spare parts inventory.</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingPOs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <FileCheck className="w-12 h-12 mb-4 opacity-20" />
                  <p>No pending POs to receive</p>
                  <p className="text-sm mt-2">Create a Purchase Order from the Maintenance department to see items here</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>PO Number</TableHead>
                      <TableHead>Item Name</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead className="text-center">Ordered</TableHead>
                      <TableHead className="text-center">Received</TableHead>
                      <TableHead className="text-center">Pending</TableHead>
                      <TableHead>Expected</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingPOs.map((item, idx) => (
                      <TableRow key={`${item.po_id}-${item.item_index}`}>
                        <TableCell className="font-mono">{item.po_number}</TableCell>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell>{item.vendor_name || '-'}</TableCell>
                        <TableCell className="text-center">{item.quantity_ordered} {item.unit}</TableCell>
                        <TableCell className="text-center">{item.quantity_received} {item.unit}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{item.pending_quantity} {item.unit}</Badge>
                        </TableCell>
                        <TableCell>{item.delivery_date ? formatDate(item.delivery_date) : '-'}</TableCell>
                        <TableCell>
                          <Button size="sm" onClick={() => openReceivePODialog(item)} data-testid={`receive-po-${idx}`}>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Receive
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Issued Tab */}
        <TabsContent value="issued" className="mt-4" data-testid="issued-tab-content">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Issuance History</CardTitle>
              <CardDescription>Date-wise tracking of all spare parts issued</CardDescription>
            </CardHeader>
            <CardContent>
              {allIssues.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No issuance records yet.</p>
              ) : (
                (() => {
                  // Group issues by spare_part_id
                  const grouped = {};
                  allIssues.forEach(issue => {
                    const key = issue.spare_part_id;
                    if (!grouped[key]) {
                      grouped[key] = {
                        part_name: issue.spare_part_name || 'Unknown Part',
                        issues: [],
                        total_qty: 0,
                      };
                    }
                    grouped[key].issues.push(issue);
                    grouped[key].total_qty += issue.quantity || 0;
                  });
                  return (
                    <div className="space-y-4">
                      {Object.entries(grouped).map(([partId, data]) => (
                        <div key={partId} className="border rounded-lg" data-testid={`issued-group-${partId}`}>
                          <div className="bg-slate-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 rounded-t-lg border-b">
                            <span className="font-semibold text-sm">{data.part_name}</span>
                            <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full w-fit">
                              Total Issued: {data.total_qty} pcs
                            </span>
                          </div>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-muted/30">
                                  <TableHead className="text-xs">Date</TableHead>
                                  <TableHead className="text-xs text-right">Qty</TableHead>
                                  <TableHead className="text-xs">Issued To</TableHead>
                                  <TableHead className="text-xs">Purpose</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {data.issues.map(issue => (
                                  <TableRow key={issue.id}>
                                    <TableCell className="text-xs whitespace-nowrap">
                                      {new Date(issue.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </TableCell>
                                    <TableCell className="text-xs text-right font-semibold">{issue.quantity}</TableCell>
                                    <TableCell className="text-xs">{issue.issued_to || '-'}</TableCell>
                                    <TableCell className="text-xs">{issue.purpose || '-'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Add Part Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Spare Part</DialogTitle>
            <DialogDescription>Enter details for the new spare part</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Part Name *</Label>
              <Input value={newPart.name} onChange={(e) => setNewPart({...newPart, name: e.target.value})} placeholder="e.g., Motor Bearing" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={newPart.description} onChange={(e) => setNewPart({...newPart, description: e.target.value})} placeholder="Optional description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Opening Quantity</Label>
                <Input type="number" value={newPart.opening_qty} onChange={(e) => setNewPart({...newPart, opening_qty: e.target.value})} placeholder="Enter quantity" />
              </div>
              <div>
                <Label>Unit</Label>
                <Input value={newPart.unit} onChange={(e) => setNewPart({...newPart, unit: e.target.value})} placeholder="pcs, kg, etc." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Stock Level</Label>
                <Input type="number" value={newPart.min_stock_level} onChange={(e) => setNewPart({...newPart, min_stock_level: e.target.value})} placeholder="Enter level" />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={newPart.location} onChange={(e) => setNewPart({...newPart, location: e.target.value})} placeholder="e.g., Store Room A" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddPart} disabled={!newPart.name}>Add Part</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Issue Part Dialog */}
      <Dialog open={showIssueDialog} onOpenChange={setShowIssueDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-primary" />
              Issue Spare Part
            </DialogTitle>
            <DialogDescription>Issue {selectedPart?.name} from inventory</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Current Stock Info */}
            <div className="p-3 bg-muted rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Part Name</p>
                  <p className="font-semibold">{selectedPart?.name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Available Stock</p>
                  <p className="font-semibold text-green-600">{selectedPart?.available_stock || selectedPart?.current_stock || 0} {selectedPart?.unit}</p>
                </div>
              </div>
            </div>
            <div>
              <Label>Quantity to Issue *</Label>
              <Input 
                type="number" 
                value={issueForm.quantity} 
                onChange={(e) => setIssueForm({...issueForm, quantity: e.target.value})} 
                placeholder="Enter quantity"
                autoFocus
              />
              <p className="text-xs text-muted-foreground mt-1">Max: {selectedPart?.available_stock || selectedPart?.current_stock || 0} {selectedPart?.unit}</p>
            </div>
            <div>
              <Label>Purpose / Machine Reference *</Label>
              <Input value={issueForm.purpose} onChange={(e) => setIssueForm({...issueForm, purpose: e.target.value})} placeholder="e.g., Loom Machine #5 maintenance" />
            </div>
            <div>
              <Label>Issued To</Label>
              <Input value={issueForm.issued_to} onChange={(e) => setIssueForm({...issueForm, issued_to: e.target.value})} placeholder="Person/team name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIssueDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleIssuePart} 
              disabled={!issueForm.quantity || !issueForm.purpose || parseQty(issueForm.quantity) > (selectedPart?.available_stock || selectedPart?.current_stock || 0) || parseQty(issueForm.quantity) <= 0}
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Issue Part
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Send for Repair Dialog */}
      <Dialog open={showRepairSendDialog} onOpenChange={setShowRepairSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send for Repair</DialogTitle>
            <DialogDescription>Send {selectedPart?.name} for external repair</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Quantity *</Label>
              <Input type="number" value={repairSendForm.quantity} onChange={(e) => setRepairSendForm({...repairSendForm, quantity: e.target.value})} placeholder="Enter quantity" />
            </div>
            <div>
              <Label>Repair Vendor *</Label>
              <Input value={repairSendForm.repair_vendor} onChange={(e) => setRepairSendForm({...repairSendForm, repair_vendor: e.target.value})} placeholder="Vendor name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sent Date *</Label>
                <Input type="date" value={repairSendForm.sent_date} onChange={(e) => setRepairSendForm({...repairSendForm, sent_date: e.target.value})} />
              </div>
              <div>
                <Label>Expected Return Date *</Label>
                <Input type="date" value={repairSendForm.expected_return_date} onChange={(e) => setRepairSendForm({...repairSendForm, expected_return_date: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={repairSendForm.notes} onChange={(e) => setRepairSendForm({...repairSendForm, notes: e.target.value})} placeholder="Optional notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRepairSendDialog(false)}>Cancel</Button>
            <Button onClick={handleSendForRepair} disabled={!repairSendForm.quantity || !repairSendForm.repair_vendor || !repairSendForm.sent_date || !repairSendForm.expected_return_date}>Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Receive from Repair Dialog */}
      <Dialog open={showRepairReceiveDialog} onOpenChange={setShowRepairReceiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Receive from Repair</DialogTitle>
            <DialogDescription>Record returned quantity from repair</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Received Quantity *</Label>
              <Input type="number" value={repairReceiveForm.received_qty} onChange={(e) => setRepairReceiveForm({...repairReceiveForm, received_qty: e.target.value})} placeholder="Enter quantity" />
            </div>
            <div>
              <Label>Received Date *</Label>
              <Input type="date" value={repairReceiveForm.received_date} onChange={(e) => setRepairReceiveForm({...repairReceiveForm, received_date: e.target.value})} />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={repairReceiveForm.notes} onChange={(e) => setRepairReceiveForm({...repairReceiveForm, notes: e.target.value})} placeholder="Optional notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRepairReceiveDialog(false)}>Cancel</Button>
            <Button onClick={handleReceiveFromRepair} disabled={!repairReceiveForm.received_qty || !repairReceiveForm.received_date}>Receive</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Spare Part Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Spare Part</DialogTitle>
            <DialogDescription>Update spare part details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Part Name *</Label>
              <Input value={editPart.name} onChange={(e) => setEditPart({...editPart, name: e.target.value})} placeholder="e.g., Motor Bearing SKF 6205" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={editPart.description} onChange={(e) => setEditPart({...editPart, description: e.target.value})} placeholder="Optional description" />
            </div>
            <div>
              <Label>Min Stock Level</Label>
              <Input type="number" value={editPart.min_stock_level} onChange={(e) => setEditPart({...editPart, min_stock_level: e.target.value})} placeholder="Alert threshold" />
            </div>
            <div>
              <Label>Location</Label>
              <Input value={editPart.location} onChange={(e) => setEditPart({...editPart, location: e.target.value})} placeholder="e.g., Store Room A" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditPart} disabled={!editPart.name}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Receive PO Dialog */}
      <Dialog open={showReceivePODialog} onOpenChange={setShowReceivePODialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Receive PO Item</DialogTitle>
            <DialogDescription>
              Receive "{selectedPOItem?.item_name}" from PO {selectedPOItem?.po_number} into spare parts inventory
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Ordered</p>
                  <p className="font-medium">{selectedPOItem?.quantity_ordered} {selectedPOItem?.unit}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Already Received</p>
                  <p className="font-medium">{selectedPOItem?.quantity_received} {selectedPOItem?.unit}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Pending</p>
                  <p className="font-medium text-orange-600">{selectedPOItem?.pending_quantity} {selectedPOItem?.unit}</p>
                </div>
              </div>
            </div>
            <div>
              <Label>Quantity to Receive *</Label>
              <Input 
                type="number" 
                value={receivePOForm.quantity} 
                onChange={(e) => setReceivePOForm({...receivePOForm, quantity: e.target.value})} 
                placeholder="Enter quantity"
                max={selectedPOItem?.pending_quantity}
              />
              <p className="text-xs text-muted-foreground mt-1">Max: {selectedPOItem?.pending_quantity} {selectedPOItem?.unit}</p>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={receivePOForm.notes} onChange={(e) => setReceivePOForm({...receivePOForm, notes: e.target.value})} placeholder="Optional notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceivePODialog(false)}>Cancel</Button>
            <Button 
              onClick={handleReceivePO} 
              disabled={!receivePOForm.quantity || parseInt(receivePOForm.quantity) <= 0 || parseInt(receivePOForm.quantity) > selectedPOItem?.pending_quantity}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Receive & Add to Inventory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Issue Dialog */}
      <Dialog open={showEditIssueDialog} onOpenChange={setShowEditIssueDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Issue Record</DialogTitle>
            <DialogDescription>Update the issue record details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Quantity *</Label>
              <Input 
                type="number" 
                value={editIssueForm.quantity} 
                onChange={(e) => setEditIssueForm({...editIssueForm, quantity: e.target.value})} 
                placeholder="Enter quantity" 
              />
            </div>
            <div>
              <Label>Purpose / Machine Reference *</Label>
              <Input 
                value={editIssueForm.purpose} 
                onChange={(e) => setEditIssueForm({...editIssueForm, purpose: e.target.value})} 
                placeholder="e.g., Loom Machine #5 maintenance" 
              />
            </div>
            <div>
              <Label>Issued To</Label>
              <Input 
                value={editIssueForm.issued_to} 
                onChange={(e) => setEditIssueForm({...editIssueForm, issued_to: e.target.value})} 
                placeholder="Person/team name" 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditIssueDialog(false)}>Cancel</Button>
            <Button onClick={handleEditIssue} disabled={!editIssueForm.quantity || !editIssueForm.purpose}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Repair Dialog */}
      <Dialog open={showEditRepairDialog} onOpenChange={setShowEditRepairDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Repair Record</DialogTitle>
            <DialogDescription>Update repair details for {editingRepair?.spare_part_name || selectedPart?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Quantity *</Label>
              <Input 
                type="number" 
                value={editRepairForm.quantity} 
                onChange={(e) => setEditRepairForm({...editRepairForm, quantity: e.target.value})} 
                placeholder="Enter quantity" 
                data-testid="edit-repair-quantity"
              />
            </div>
            <div>
              <Label>Vendor *</Label>
              <Input 
                value={editRepairForm.repair_vendor} 
                onChange={(e) => setEditRepairForm({...editRepairForm, repair_vendor: e.target.value})} 
                placeholder="Repair vendor name"
                data-testid="edit-repair-vendor"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sent Date</Label>
                <Input 
                  type="date" 
                  value={editRepairForm.sent_date} 
                  onChange={(e) => setEditRepairForm({...editRepairForm, sent_date: e.target.value})}
                  data-testid="edit-repair-sent-date"
                />
              </div>
              <div>
                <Label>Expected Return</Label>
                <Input 
                  type="date" 
                  value={editRepairForm.expected_return_date} 
                  onChange={(e) => setEditRepairForm({...editRepairForm, expected_return_date: e.target.value})}
                  data-testid="edit-repair-expected-date"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input 
                value={editRepairForm.notes} 
                onChange={(e) => setEditRepairForm({...editRepairForm, notes: e.target.value})} 
                placeholder="Optional notes"
                data-testid="edit-repair-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditRepairDialog(false)}>Cancel</Button>
            <Button onClick={handleEditRepair} disabled={!editRepairForm.quantity || !editRepairForm.repair_vendor} data-testid="save-edit-repair-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Stock Dialog (Admin only) */}
      <Dialog open={showAddStockDialog} onOpenChange={setShowAddStockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Stock</DialogTitle>
            <DialogDescription>
              Manually add stock for <strong>{addStockPart?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg text-sm">
              <p>Current Stock: <strong>{addStockPart?.current_stock || 0} {addStockPart?.unit}</strong></p>
              <p>Opening Qty: <strong>{addStockPart?.opening_qty || 0} {addStockPart?.unit}</strong></p>
            </div>
            <div>
              <Label>Quantity to Add *</Label>
              <Input 
                type="number" 
                value={addStockForm.quantity} 
                onChange={(e) => setAddStockForm({...addStockForm, quantity: e.target.value})} 
                placeholder="Enter quantity" 
                min="1"
                data-testid="add-stock-quantity"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Input 
                value={addStockForm.notes} 
                onChange={(e) => setAddStockForm({...addStockForm, notes: e.target.value})} 
                placeholder="Reason for adding stock (optional)"
                data-testid="add-stock-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStockDialog(false)}>Cancel</Button>
            <Button onClick={handleAddStock} disabled={!addStockForm.quantity || parseInt(addStockForm.quantity) <= 0} data-testid="confirm-add-stock-btn">
              Add Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
